"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { searchPlacesAutocomplete, generateSessionToken } from "@/lib/utils/places-api";

/**
 * Custom hook for Google Places Autocomplete search
 * @param {string} input - Search query string
 * @param {Object} options - Configuration options
 * @param {number} options.debounceMs - Debounce delay in milliseconds (default: 300)
 * @returns {Object} { predictions, isLoading, error }
 */
export function usePlacesAutocomplete(input, options = {}) {
  const {
    debounceMs = 300
  } = options;
  const [predictions, setPredictions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const sessionTokenRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const createSessionToken = useCallback(() => {
    sessionTokenRef.current = generateSessionToken();
  }, []);
  const searchPlaces = useCallback(async (query, retryCount = 0) => {
    if (!query.trim()) {
      setPredictions([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    if (!sessionTokenRef.current) {
      createSessionToken();
    }
    const maxRetries = 2;
    const retryDelay = 1000; // 1 second

    try {
      const placePredictions = await searchPlacesAutocomplete({
        input: query,
        sessionToken: sessionTokenRef.current
      });
      setPredictions(placePredictions);
      setError(null);
    } catch (err) {
      // Don't retry for certain errors
      const isNonRetryableError = err.message?.includes("REQUEST_DENIED") || err.message?.includes("permission") || err.message?.includes("403") || err.message?.includes("API key") || err.message?.includes("OVER_QUERY_LIMIT") || err.message?.includes("quota") || err.message?.includes("429");

      // Retry logic for retryable errors
      if (!isNonRetryableError && retryCount < maxRetries) {
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        // Retry with incremented count
        return searchPlaces(query, retryCount + 1);
      }

      // Set error message
      setPredictions([]);
      const errorMessage = isNonRetryableError ? err.message?.includes("REQUEST_DENIED") || err.message?.includes("permission") || err.message?.includes("403") ? "Places API request denied. Please check your API key permissions." : err.message?.includes("OVER_QUERY_LIMIT") || err.message?.includes("quota") || err.message?.includes("429") ? "Places API quota exceeded. Please try again later." : err.message?.includes("API key") ? "Invalid API key. Please check your configuration." : "Unable to search locations. Please try again." : retryCount >= maxRetries ? "Unable to search locations. Please check your connection and try again." : "Unable to search locations. Please try again.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [createSessionToken]);
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (!input || !input.trim()) {
      setPredictions([]);
      setIsLoading(false);
      setError(null);
      return;
    }
    debounceTimerRef.current = setTimeout(() => {
      setIsLoading(true);
      searchPlaces(input);
    }, debounceMs);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [input, debounceMs, searchPlaces]);
  return {
    predictions,
    isLoading,
    error
  };
}